import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./InputField.module.scss');

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'>;

type InputSize = 'S' | 'L';

const SIZE_MAP: Record<InputSize, string> = {
  S: 'small',
  L: 'large',
};

type UserType = 'talent' | 'company' | 'neutral';

export interface InputFieldProps extends NativeInputProps {
  autoSaveUrl?: string;
  containerClassName?: string;
  error?: string;
  hint?: string;
  iconLeft?: string;
  iconRight?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  size?: InputSize;
  showVisibilityToggle?: boolean;
  context?: UserType;
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
  defaultValidationMessage?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  autoSaveUrl,
  containerClassName,
  error,
  hint,
  iconLeft,
  iconRight,
  label,
  required,
  placeholder,
  size = 'S',
  showVisibilityToggle = false,
  context = 'neutral',
  inputRef,
  disabled,
  id,
  type = 'text',
  defaultValidationMessage,
  ...inputProps
}) => {
  const { autoSaveError, save } = useAutoSave({ url: autoSaveUrl || '', name: inputProps.name || '' });
  const [isVisible, setIsVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [displayError, setDisplayError] = useState(error || '');
  useEffect(() => { setDisplayError(error || ''); }, [error]);
  const isControlled = inputProps.onChange !== undefined;

  useEffect(() => { if (autoSaveError) setDisplayError(autoSaveError); }, [autoSaveError]);
  const sizeClass = SIZE_MAP[size];
  const inputId = id || `input-field-${React.useId ? React.useId() : Math.random().toString(36).slice(2)}`;

  useEffect(() => {
    setDisplayError(error || '');
  }, [error]);

  const shouldToggleVisibility = (type === 'password' || showVisibilityToggle) && !disabled;
  const resolvedType = shouldToggleVisibility ? (isVisible ? 'text' : 'password') : type;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);

    if (inputProps.onFocus) {
      inputProps.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    if (required && (!e.target.value || e.target.value.trim() === '')) {
      setDisplayError(defaultValidationMessage);
    }

    if (inputProps.onBlur) {
      inputProps.onBlur(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayError('');
    if (autoSaveUrl) save(e.target.value);
    if (inputProps.onChange) inputProps.onChange(e);
  };

  const showHint = isFocused && Boolean(hint) && !displayError;
  const hasMessage = Boolean(displayError) || showHint;
  const messageId = hasMessage ? `${inputId}-message` : undefined;

  const inputContainerClasses = classNames(
    styles.inputContainer,
    {
      [styles[sizeClass]]: sizeClass,
      [styles.error]: Boolean(displayError),
      [styles.disabled]: disabled,
      [styles[context]]: context,
    }
  );

  const wrapperClasses = classNames(
    styles.wrapper,
    containerClassName
  );

  const { onBlur: _onBlur, onChange: _onChange, onFocus: _onFocus, value: nativeValue, ...safeInputProps } = inputProps;

  return (
    <div className={wrapperClasses}>
      {label && (
        <div className={styles.labelContainer}>
          <label htmlFor={inputId} className={styles.label}>{label}</label>
          {required && <span className={styles.required} aria-hidden="true">*</span>}
        </div>
      )}

      <div className={styles.inputWrapper}>
        <div className={inputContainerClasses}>
          {iconLeft && (
            <div className={styles.iconLeftContainer}>
              <Icon
                name={iconLeft}
                size="md"
                color='black'
                aria-hidden='true'
              />
            </div>
          )}

          <input
            id={inputId}
            type={resolvedType}
            className={styles.input}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            aria-invalid={Boolean(displayError)}
            aria-describedby={messageId}
            ref={inputRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...safeInputProps}
            {...(!isControlled && nativeValue !== undefined
              ? { defaultValue: nativeValue }
              : isControlled ? { value: nativeValue } : {}
            )}
          />

          {shouldToggleVisibility && (
            <button
              type='button'
              className={styles.visibilityToggle}
              aria-label={isVisible ? 'Hide value' : 'Show value'}
              aria-pressed={isVisible}
              onClick={() => setIsVisible(value => !value)}
            >
              <Icon
                name={isVisible ? 'eyeOff' : 'eye'}
                color='black'
                size="md"
                aria-hidden="true"
              />
            </button>
          )}

          {iconRight && !shouldToggleVisibility && (
            <div className={styles.iconRightContainer}>
              <Icon
                name={iconRight}
                size="md"
                color='black'
                aria-hidden='true'
              />
            </div>
          )}
        </div>

        {hasMessage && (
          <div
            id={messageId}
            role={displayError ? 'alert' : undefined}
            className={classNames(styles.hintContainer, {
              [styles.hintError]: Boolean(displayError),
            })}
          >
            <Icon
              name={displayError ? 'triangleAlert' : 'info'}
              color={displayError ? 'error' : 'disable'}
              size="sm"
              aria-hidden='true'
            />
            <p className={styles.hintText}>{displayError || hint}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputField;
